import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import i18n from "../i18n";

import { newId } from "../utils/ids";
import { getInitialMessages, getInitialCTAs } from "./chat.constants";
import {
  buildMessagesWithTyping,
  buildCartItems,
  buildCartSummary,
} from "./chat.selectors";

import {
  syncCartFromServer,
  syncGuestCartToServer,
} from "./session/session.cart";
import {
  callChatApi,
  loadChatSession,
  syncGuestChatToServer,
} from "./session/session.chat";

import { dispatchSessionAction } from "./session/session.actions";
import { saveChatMessage } from "../api/chatApi";

export function useChatSession() {
  const [chatId, setChatId] = useState(() => {
  return localStorage.getItem("guest_chat_id") || newId();
});
  const [screen, setScreen] = useState("chat");

  const [messages, setMessages] = useState(() => {
  try {
    const saved = localStorage.getItem("guest_chat_messages");

    return saved
      ? JSON.parse(saved)
      : getInitialMessages();
  } catch (e) {
    console.error("guest chat init error:", e);
    return getInitialMessages();
  }
});
  const [ctas, setCtas] = useState(() => getInitialCTAs());

  const [cart, setCart] = useState(() => {
  try {
    const saved = localStorage.getItem("guest_cart");
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error("guest cart init error:", e);
    return {};
  }
});
  const [loading, setLoading] = useState(false);

  const reqSeqRef = useRef(0);
  const productIndexRef = useRef({});

  const [preset, setPreset] = useState("dine_in");

  const [typingText, setTypingText] = useState("");

  const messagesWithTyping = useMemo(
  () => buildMessagesWithTyping(messages, loading, typingText),
  [messages, loading, typingText]
);

  const cartItems = useMemo(() => buildCartItems(cart), [cart]);
  const cartSummary = useMemo(() => buildCartSummary(cartItems), [cartItems]);

  useEffect(() => {
  if (auth.currentUser) return;

  try {
    localStorage.setItem("guest_chat_id", chatId);
    localStorage.setItem(
      "guest_chat_messages",
      JSON.stringify(messages)
    );
  } catch (e) {
    console.error("guest chat save error:", e);
  }
}, [chatId, messages]);

useEffect(() => {
  if (auth.currentUser) return;

  try {
    localStorage.setItem("guest_cart", JSON.stringify(cart));
  } catch (e) {
    console.error("guest cart save error:", e);
  }
}, [cart]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
  return;
}

      try {
        await syncGuestCartToServer();
await syncCartFromServer({ setCart });

const syncedChatId = await syncGuestChatToServer();

if (syncedChatId) {
  setChatId(syncedChatId);

  window.dispatchEvent(
    new CustomEvent("chatshop:chats-updated")
  );
}
      } catch (e) {
        console.error("syncCartFromServer error:", e);
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    function handleLanguageChanged() {
      setMessages((prev) => {
        const isWelcomeOnly =
          prev.length === 1 &&
          prev[0]?.role === "assistant" &&
          !prev[0]?.text &&
          Array.isArray(prev[0]?.parts);

        return isWelcomeOnly ? getInitialMessages() : prev;
      });

      setCtas(getInitialCTAs());
    }

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  

  async function callChat(text, historyOverride) {
    await callChatApi({
      text,
      historyOverride,
      chatId,
      cart,
      reqSeqRef,
      setLoading,
      setMessages,
      setCtas,
      setScreen,
      setCart,
      productIndexRef,
    });
  }

  async function send(text) {
    const t = String(text || "").trim();
    if (!t) return;

    const isWelcomeOnly =
      messages.length === 1 &&
      messages[0]?.role === "assistant" &&
      !messages[0]?.text &&
      Array.isArray(messages[0]?.parts);

    const baseHistory = isWelcomeOnly ? [] : messages;
    const nextHistory = [...baseHistory, { role: "user", text: t }];

    setMessages(nextHistory);

    const isCartLike =
  /(додай|добав|поклади|кинь|видали|прибери|очисти|корзин|кошик|add|remove|delete|clear|cart)/i.test(
    t
  );

const isProductLike =
  /(пиво|сидр|лимонад|напій|товар|продукт|снек|чіпс|горіш|порадь|покажи|beer|cider|lemonade|drink|product|snack|recommend|show)/i.test(
    t
  );

setTypingText("");

const typingTimer = setTimeout(() => {
  const isEn = String(i18n.language || "")
    .toLowerCase()
    .startsWith("en");

  setTypingText(
    isCartLike
      ? isEn
        ? "Updating cart…"
        : "Оновлюємо корзину…"
      : isProductLike
      ? isEn
        ? "Finding products…"
        : "Підбираємо товари…"
      : isEn
      ? "Thinking about the answer…"
      : "Думаю над відповіддю…"
  );
}, 2000);

try {
  await callChat(t, nextHistory);
} finally {
  clearTimeout(typingTimer);
  setTypingText("");
}

    
  }

  async function appendOrderConfirmation(order, forcedChatId = null) {
  if (!order) return;

  const orderId = String(
    order.id || order.receipt_number || order.number || Date.now()
  );

  const isUk = (i18n.resolvedLanguage || i18n.language || "uk")
    .toLowerCase()
    .startsWith("uk");

  const text = isUk
    ? `Привіт!
Дякуємо за ваше замовлення — ми успішно його отримали.

Якщо у вас виникнуть питання — просто напишіть нам, ми з радістю допоможемо.

Дякуємо, що обрали нас!`
    : `Hi there!
Thank you for your order — we’ve received it successfully.

If you have any questions, just reply here — we’re happy to help.

Thanks again for shopping with us!`;

  const message = {
    id: `order-confirmation-${orderId}`,
    role: "assistant",
    text,
    blocks: [
      {
        type: "order_card",
        order: {
          id: order.id || null,
          receipt_number: order.receipt_number || "",
          date: order.date || order.created_at || "",
          status: order.status || "processing",
          total: Number(order.total || 0),
          items: Array.isArray(order.items) ? order.items : [],
          tracking: order.tracking || "",
        },
      },
    ],
    meta: {
      kind: "order_confirmation",
      orderId,
    },
  };

  const alreadyExists = messages.some(
    (msg) =>
      msg?.meta?.kind === "order_confirmation" &&
      String(msg?.meta?.orderId) === orderId
  );

  if (alreadyExists) return;

  setMessages((prev) => [...prev, message]);
  setScreen("chat");

  try {
    await saveChatMessage(forcedChatId || chatId, message);
  } catch (e) {
    console.error("save order confirmation error:", e);
  }
}

  async function onCtaClick(cta) {
    if (cta?.disabled) return;
    const action = cta?.action || {};
    await onAction(action.type ? action : { type: "send", message: cta.label });
  }

  async function onAction(action) {
    return dispatchSessionAction({
      action,
      send,
      setScreen,
      cart,
      setCart,
      setMessages,
      productIndexRef,
    });
  }

  async function loadChat(externalId) {
    await loadChatSession({
      externalId,
      setLoading,
      setChatId,
      setMessages,
      setCtas,
      setScreen,
      productIndexRef,
    });
  }

  function startNewChat() {
    setChatId(newId());
    setMessages(getInitialMessages());
    setCtas(getInitialCTAs());
    setScreen("chat");
  }

  const resetSession = useCallback(() => {
    setChatId(newId());
    setMessages(getInitialMessages());
    setCtas(getInitialCTAs());
    setScreen("chat");
    //setCart({});
  }, []);

  return {
    chatId,
    setChatId,
    loadChat,
    startNewChat,
    resetSession,

    screen,
    setScreen,

    messagesWithTyping,
    ctas,
    loading,

    cart,
    cartItems,
    cartSummary,

    preset,
    setPreset,

    send,
    onCtaClick,
    onAction,
    appendOrderConfirmation,
  };
}